import type {AudioData} from '@Libraries/add-media-library';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {repoURL} from '@Libraries/s3-library';
import {getAssetUrl, getWriteBucket} from '@Utils/s3.util';
import type {AiGeneratedContent, RecentlyGeneratedAiContent, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';

const BASE_DIR = 'aispeech';
const VOICE_PRESET_DIR = 'aivoice/voice-presets';
export const GENERATED_SPEECH_FILE_EXTENSION = 'mp3';
export const GENERATE_SPEECH_DAILY_LIMIT_REACHED = 'daily_limit_reached_for_user';
const GENERATED_VOICE_LOADING_KEY = 'generateVoiceLoading';

export interface GenerateSpeechParams {
  text: string;
  voiceId: number;
}

export interface GenerateAISpeechResponse {
  id: number;
  filename: string;
  src: string;
  ext: string;
  name: string;
  duration: number;
  iduploader: number;
  used_on: number;
  generatedOn: number;
  idVoice: number;
}

export const generateSpeech = async (params: GenerateSpeechParams): Promise<GenerateAISpeechResponse> => {
  trackPosterBuilderGA4Events(GA4EventName.AI_VOICE_GENERATED);
  return (await window.PMW.writeLocal('aivoice/generateSpeech', params)) as GenerateAISpeechResponse;
};

const uploadGeneratedSpeech = async (idGeneratedSpeech: number): Promise<AudioData> => {
  return (await window.PMW.writeLocal('aivoice/uploadGeneratedSpeech', {idUserGeneratedAudio: idGeneratedSpeech})) as AudioData;
};

const addGeneratedSpeechToPoster = async (generatedSpeech: AudioData): Promise<void> => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  await currentPage.items.addItems.addAudioItems([generatedSpeech], {});
  trackPosterBuilderGA4Events(GA4EventName.AI_VOICE_ADDED);
};

export const getUrlForGeneratedAiSpeech = (hashedFilename: string, ext: string = GENERATED_SPEECH_FILE_EXTENSION): string => {
  return repoURL(`${BASE_DIR}/${hashedFilename}.${ext}`, getWriteBucket());
};

export const getUrlForVoicePreset = (voicePresetId: number): string => {
  return getAssetUrl(`${VOICE_PRESET_DIR}/${voicePresetId}.${GENERATED_SPEECH_FILE_EXTENSION}?ts=1787036254`);
};

export const getUserGeneratedSpeech = async (offset: number, limit: number): Promise<GenerateAISpeechResponse[]> => {
  return (await window.PMW.readLocal('aivoice/getGeneratedSpeeches', {limit, offset})) as GenerateAISpeechResponse[];
};

export const writeTranscript = async (prompt: string, selectedValues: SelectedValueType): Promise<AiGeneratedContent[]> => {
  return (await window.PMW.writeLocal('aivoice/write', {text: prompt, filters: selectedValues})) as AiGeneratedContent[];
};

export const reWriteTranscript = async (idGeneration: string, selectedValues: SelectedValueType): Promise<AiGeneratedContent> => {
  return (await window.PMW.writeLocal('aivoice/rewrite', {idWriting: idGeneration, filters: selectedValues})) as AiGeneratedContent;
};

export const saveSelectedWriting = async (idPrompt: number, text: string): Promise<void> => {
  await window.PMW.writeLocal('aivoice/saveSelectedWriting', {idPrompt, text});
};

export const getRecentlyGeneratedTranscripts = async (limit: number, offset: number): Promise<RecentlyGeneratedAiContent[]> => {
  return (await window.PMW.readLocal('aivoice/getGeneratedWritings', {limit, offset})) as RecentlyGeneratedAiContent[];
};

export const getGeneratedTranscript = async (id: number): Promise<RecentlyGeneratedAiContent | null> => {
  return (await window.PMW.readLocal('aivoice/getGeneratedWritingForId', {id})) as RecentlyGeneratedAiContent;
};

export const getHasUserSeenAiVoice = async (): Promise<boolean> => {
  return !!(await window.PMW.readLocal('aivoice/hasUserSeenAiVoice'));
};

export const markUserHasSeenAiVoice = async (): Promise<void> => {
  await window.PMW.writeLocal('aivoice/markUserHasSeenAiVoice');
};

export const showTextToSpeechUpsellDialog = async (): Promise<void> => {
  await window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_SPEECH as string);
};

export const showTextToSpeechAiWriterUpsellDialog = async (): Promise<void> => {
  await window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_SPEECH_AI_WRITER as string);
};

const runGeneratedVoiceUpload = async (idGeneratedVoice: number, consumeAudioData: (audioData: AudioData) => Promise<void>): Promise<void> => {
  showLoading(GENERATED_VOICE_LOADING_KEY, {
    text: window.i18next.t('pmwjs_adding_1_of_1'),
  });

  try {
    const audioData = await uploadGeneratedSpeech(idGeneratedVoice);
    await consumeAudioData(audioData);
  } catch (e) {
    console.error(e);
  } finally {
    hideLoading(GENERATED_VOICE_LOADING_KEY);
  }
};

export const addGeneratedVoiceToDesign = async (idGeneratedVoice: number): Promise<void> => {
  await runGeneratedVoiceUpload(idGeneratedVoice, addGeneratedSpeechToPoster);
};

export const replaceAudioItemWithGeneratedVoice = async (audioItemIdToReplace: string, idGeneratedVoice: number): Promise<void> => {
  const replaceAudioClipWithGeneratedVoice = async (audioData: AudioData): Promise<void> => {
    await window.posterEditor?.whiteboard?.getCurrentPage().audioClips.replaceAudioItem(audioItemIdToReplace, audioData);
  };
  await runGeneratedVoiceUpload(idGeneratedVoice, replaceAudioClipWithGeneratedVoice);
};
